// The toolkit contract as the console's Toolkits surface reads it
// (02-DESIGN/08-toolkits/01-the-toolkit-contract.md; decision 0014): the
// signature a toolkit registers under — one service name for every toolkit,
// so one scatter finds them all — its scope and its scheme, the link or the
// setup a client derives the experience from, its tools with their schemas,
// and the key a call is sealed toward. Discovery is the catalogue: a caller
// scatters `$SRV.INFO.toolkit` on its own connection and keeps what answered.
// The console calls no tool; the runtime that serves one and the caller that
// calls one are the Go library's.
import {} from "@nats-io/nats-core";
import { utf8Decode } from "./encoding.js";
import { CODE_CANNOT_OPEN, METADATA_KEY } from "./envelope.js";
import { judge } from "./identity.js";
import { newInbox } from "./inbox.js";
import { address, Family, serviceInfo, TOKEN_TOOL, workloadTool } from "./subject.js";
import { personPath, workloadPath } from "./vault.js";
/** SERVICE is the micro service name every toolkit registers as, so that one scatter finds them all. */
export const SERVICE = "toolkit";
/** Scope says whom the upstream sees on a call: the caller, one shared identity, or nobody. */
export const Scope = {
    person: "person",
    tenant: "tenant",
    none: "none",
};
/** validScope reports whether s is one of the three. */
export function validScope(s) {
    return s === Scope.person || s === Scope.tenant || s === Scope.none;
}
/**
 * Scheme is how a toolkit is credentialed: a refresh token the person linked
 * and the toolkit redeems, a token the person or an admin pasted, or nothing.
 * The scheme is the last element of the vault path a person-scoped toolkit
 * reads, so a client that knows the scheme knows the path.
 */
export const Scheme = {
    oauthRefresh: "oauth-refresh",
    token: "token",
    none: "none",
};
/** validScheme reports whether s is one of the three. */
export function validScheme(s) {
    return s === Scheme.oauthRefresh || s === Scheme.token || s === Scheme.none;
}
/**
 * compatible reports whether a scope and a scheme go together
 * (02-DESIGN/08-toolkits/02-the-mcp-runtime.md § the settings document):
 * none with none; token with person or tenant; oauth-refresh with person.
 */
export function compatible(scope, scheme) {
    switch (scheme) {
        case Scheme.none:
            return scope === Scope.none;
        case Scheme.token:
            return scope === Scope.person || scope === Scope.tenant;
        case Scheme.oauthRefresh:
            return scope === Scope.person;
    }
}
/** ClientType is whether the toolkit's OAuth client holds a secret: a confidential client's secret lives at the toolkit's own vault prefix and the toolkit serves the exchange verb for it. */
export const ClientType = {
    public: "public",
    confidential: "confidential",
};
/** validClientType reports whether s is one of the two. */
export function validClientType(s) {
    return s === ClientType.public || s === ClientType.confidential;
}
/** The signature's metadata keys on the service info. The schemas are JSON strings, because metadata is a flat string map. */
export const META_NAME = "toolkit.name";
/** META_WORKLOAD is the id of the workload the toolkit is: the `<id>` of its family and of its vault paths, advertised before any tool registers. */
export const META_WORKLOAD = "toolkit.workload";
export const META_SCOPE = "toolkit.scope";
export const META_SCHEME = "toolkit.auth-scheme";
export const META_LINK_PREFIX = "toolkit.link.";
export const META_LINK_AUTHORIZE_ENDPOINT = `${META_LINK_PREFIX}authorize-endpoint`;
export const META_LINK_TOKEN_ENDPOINT = `${META_LINK_PREFIX}token-endpoint`;
export const META_LINK_CLIENT_ID = `${META_LINK_PREFIX}client-id`;
export const META_LINK_SCOPES = `${META_LINK_PREFIX}scopes`;
export const META_LINK_CLIENT_TYPE = `${META_LINK_PREFIX}client-type`;
export const META_SETUP_SCHEMA = "toolkit.setup.schema";
export const META_SETUP_INSTRUCTIONS = "toolkit.setup.instructions";
/** META_SEALING_KEY is the envelope's own key, advertised by the pattern. */
export const META_SEALING_KEY = METADATA_KEY;
/** META_SETTINGS_REVISION is the revision of the settings document a platform runtime built its signature from; of two answers from one workload, the later is kept. */
export const META_SETTINGS_REVISION = "settings.revision";
/** The per-tool metadata keys on an endpoint. */
export const META_TOOL_DESCRIPTION = "tool.description";
export const META_TOOL_SCHEMA = "tool.schema";
export const META_TOOL_UPSTREAM_NAME = "tool.upstream-name";
/**
 * VERB_EXCHANGE is the reserved verb a confidential client's toolkit serves
 * inside its own family (02-DESIGN/08-toolkits/03-linking.md § the flow):
 * never a tool, never advertised as one. A tool name beginning with `_` is
 * reserved for a runtime's own verbs.
 */
export const VERB_EXCHANGE = "_exchange";
/** RESERVED is the prefix of a runtime's own verbs in the tool position. */
export const RESERVED = "_";
/** validName reports whether s is a toolkit name: `[a-z0-9-]+`, a subject token and a path element both. */
export function validName(s) {
    return /^[a-z0-9-]+$/.test(s);
}
/** validToolName reports whether s is a tool name: the name grammar, and not a reserved verb. */
export function validToolName(s) {
    return validName(s) && !s.startsWith(RESERVED);
}
/** subject is a tool's subject: `workload.<id>.tool.<tool>`. */
export function subject(workload, tool) {
    return workloadTool(workload, tool);
}
/** exchangeSubject is the exchange verb's subject: `workload.<id>.tool._exchange`. */
export function exchangeSubject(workload) {
    return workloadTool(workload, VERB_EXCHANGE);
}
/** tool looks a tool up by name. */
export function tool(tk, name) {
    return tk.tools.find((t) => t.name === name);
}
/** The contract's error codes. */
export const Code = {
    argsInvalid: "args-invalid",
    identityRequired: "identity-required",
    identityInvalid: "identity-invalid",
    guardDenied: "guard-denied",
    guardEscalated: "guard-escalated",
    upstreamAuthFailed: "upstream-auth-failed",
    upstreamForbidden: "upstream-forbidden",
    upstreamUnavailable: "upstream-unavailable",
    upstreamOutcomeUnknown: "upstream-outcome-unknown",
    payloadTooLarge: "payload-too-large",
    busy: "busy",
    cannotOpen: CODE_CANNOT_OPEN,
};
/** NoToolkitError is thrown when no toolkit on the connection carries the name. */
export class NoToolkitError extends Error {
    constructor(name) {
        super(`toolkit: no toolkit carries the name ${name}`);
        this.name = "NoToolkitError";
    }
}
/** AmbiguousToolkitError is thrown when two toolkits carry one name: the caller refuses rather than picking one. */
export class AmbiguousToolkitError extends Error {
    constructor(name) {
        super(`toolkit: two toolkits carry the name ${name}`);
        this.name = "AmbiguousToolkitError";
    }
}
/** PERSON_TOOLKIT_SEGMENT is the element under a person's namespace that holds their toolkit credentials: `person.<principal>.toolkit.…`. */
export const PERSON_TOOLKIT_SEGMENT = "toolkit";
/**
 * personCredentialPath is where a person-scoped toolkit reads the caller's
 * credential: `person.<principal>.toolkit.<id>.<scheme>` in the tenant's
 * vault, deposited by the person's own linking and admitted to this
 * toolkit's template for every person. The console computes it from the
 * scheme it read and the toolkit's workload id, and from nothing else a
 * toolkit advertises.
 */
export function personCredentialPath(principal, workload, scheme) {
    return personPath(principal, PERSON_TOOLKIT_SEGMENT, workload, scheme);
}
/** credentialPath is where a tenant-scoped toolkit reads the shared credential an admin stored: `workload.<id>.credential`. */
export function credentialPath(workload) {
    return workloadPath(workload, "credential");
}
/** clientSecretPath is where a confidential client's secret lives: `workload.<id>.client-secret`. */
export function clientSecretPath(workload) {
    return workloadPath(workload, "client-secret");
}
/** TOKEN_SETUP_SCHEMA is the setup schema of a `token` scheme whose record is one pasted bearer or API key. */
export const TOKEN_SETUP_SCHEMA = '{"type":"object","properties":{"token":{"type":"string","title":"Token"}},"required":["token"]}';
function str(v) {
    return typeof v === "string" ? v : "";
}
function stringMap(v) {
    const out = {};
    if (typeof v === "object" && v !== null) {
        for (const [k, val] of Object.entries(v)) {
            if (typeof val === "string") {
                out[k] = val;
            }
        }
    }
    return out;
}
/** linkOf reads the link back from advertised metadata; undefined when none is advertised. */
export function linkOf(md) {
    const clientId = md[META_LINK_CLIENT_ID] ?? "";
    const tokenEndpoint = md[META_LINK_TOKEN_ENDPOINT] ?? "";
    const authorizeEndpoint = md[META_LINK_AUTHORIZE_ENDPOINT] ?? "";
    if (clientId === "" && tokenEndpoint === "" && authorizeEndpoint === "") {
        return undefined;
    }
    const clientType = md[META_LINK_CLIENT_TYPE] ?? "";
    const scopes = (md[META_LINK_SCOPES] ?? "").split(/\s+/).filter((s) => s !== "");
    return {
        authorizeEndpoint,
        tokenEndpoint,
        clientId,
        scopes,
        clientType: validClientType(clientType) ? clientType : "",
    };
}
/** setupOf reads the setup back from advertised metadata; undefined when none is advertised. */
export function setupOf(md) {
    const schema = md[META_SETUP_SCHEMA] ?? "";
    const instructions = md[META_SETUP_INSTRUCTIONS] ?? "";
    if (schema === "" && instructions === "") {
        return undefined;
    }
    const s = { instructions };
    if (schema !== "") {
        s.schema = schema;
    }
    return s;
}
/**
 * parseInfo reads a service-info reply as a toolkit; undefined when the
 * service is not one. The endpoints under `workload.<id>.tool.<name>` are its
 * tools, sorted by name; a reserved verb is never a tool.
 */
export function parseInfo(data) {
    let info;
    try {
        const parsed = JSON.parse(utf8Decode(data));
        if (typeof parsed !== "object" || parsed === null) {
            return undefined;
        }
        info = parsed;
    }
    catch {
        return undefined;
    }
    if (info.name !== SERVICE) {
        return undefined;
    }
    const md = stringMap(info.metadata);
    const scope = md[META_SCOPE] ?? "";
    const scheme = md[META_SCHEME] ?? "";
    const tk = {
        id: str(info.id),
        workload: md[META_WORKLOAD] ?? "",
        version: str(info.version),
        name: md[META_NAME] ?? "",
        scope: validScope(scope) ? scope : "",
        scheme: validScheme(scheme) ? scheme : "",
        sealingKey: md[META_SEALING_KEY] ?? "",
        metadata: md,
        tools: [],
    };
    const link = linkOf(md);
    if (link) {
        tk.link = link;
    }
    const setup = setupOf(md);
    if (setup) {
        tk.setup = setup;
    }
    if (tk.name === "") {
        return undefined;
    }
    if (Array.isArray(info.endpoints)) {
        const endpoints = info.endpoints;
        for (const ep of endpoints) {
            if (typeof ep !== "object" || ep === null) {
                continue;
            }
            const subj = "subject" in ep ? str(ep.subject) : "";
            let toks;
            try {
                toks = address(subj);
            }
            catch {
                continue;
            }
            const [family, workload, reserved, name] = toks;
            if (toks.length !== 4 ||
                family !== Family.workload ||
                reserved !== TOKEN_TOOL ||
                workload === undefined ||
                name === undefined) {
                continue;
            }
            if (tk.workload === "") {
                // A runtime from before the signature named its workload: its first
                // endpoint's subject is the only place it says.
                tk.workload = workload;
            }
            if (workload !== tk.workload) {
                continue; // under another id than the one advertised: not its tool
            }
            if (name.startsWith(RESERVED)) {
                continue; // a runtime's own verb, never a tool
            }
            const epmd = stringMap("metadata" in ep ? ep.metadata : undefined);
            const t = {
                name,
                subject: subj,
                description: epmd[META_TOOL_DESCRIPTION] ?? "",
                upstreamName: epmd[META_TOOL_UPSTREAM_NAME] ?? "",
            };
            const schema = epmd[META_TOOL_SCHEMA] ?? "";
            if (schema !== "") {
                t.schema = schema;
            }
            tk.tools.push(t);
        }
    }
    tk.tools.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    return tk;
}
/** revisionOf reads the settings revision a runtime advertised; zero when none. */
function revisionOf(tk) {
    const n = Number(tk.metadata[META_SETTINGS_REVISION] ?? "");
    return Number.isSafeInteger(n) && n > 0 ? n : 0;
}
/** The server's no-responders status, delivered to the reply inbox when nothing is subscribed. */
const NO_RESPONDERS = 503;
function abortReason(signal, what) {
    const reason = signal.reason;
    return reason instanceof Error ? reason : new DOMException(`${what}: aborted`, "AbortError");
}
/** nextWithin waits for a message for at most ms; undefined when none came. */
async function nextWithin(it, ms, signal) {
    if (signal?.aborted) {
        throw abortReason(signal, "toolkit");
    }
    let timer;
    let onAbort;
    try {
        const result = await Promise.race([
            it.next(),
            new Promise((resolve) => {
                timer = setTimeout(() => {
                    resolve(undefined);
                }, ms);
            }),
            new Promise((_, reject) => {
                if (signal) {
                    onAbort = () => {
                        reject(abortReason(signal, "toolkit"));
                    };
                    signal.addEventListener("abort", onAbort, { once: true });
                }
            }),
        ]);
        if (result === undefined || result.done === true) {
            return undefined;
        }
        return result.value;
    }
    finally {
        clearTimeout(timer);
        if (signal && onAbort) {
            signal.removeEventListener("abort", onAbort);
        }
    }
}
/**
 * discover scatters `$SRV.INFO.toolkit` on the caller's own connection, on an
 * inbox inside the fence, and answers every toolkit the connection can see,
 * in the order they answered, keeping only a toolkit the platform vouches
 * for — its attestation verifies against keys and names tenant, the
 * toolkit's name, workload and key (decision 0026); any other answer is
 * dropped uncounted; an unattested one is kept, when
 * lenient, for a name no
 * attested toolkit carries. Two answers from one workload — a runtime
 * re-registering beside the one it drains — are one toolkit: the attested of
 * the two where one is, else the later by `settings.revision`. The scatter ends when no answer comes within the
 * stall, when the server says nobody is subscribed, or at the timeout.
 */
export async function discover(nc, opts = {}) {
    const stall = opts.stall ?? 250;
    const deadline = Date.now() + (opts.timeout ?? 5000);
    const inbox = newInbox(nc);
    const sub = nc.subscribe(inbox);
    const it = sub[Symbol.asyncIterator]();
    const byWorkload = new Map();
    try {
        nc.publish(serviceInfo(SERVICE), undefined, { reply: inbox });
        for (;;) {
            const wait = Math.min(stall, deadline - Date.now());
            if (wait <= 0) {
                break;
            }
            const msg = await nextWithin(it, wait, opts.signal);
            if (msg === undefined || (msg.headers?.code ?? 0) === NO_RESPONDERS) {
                break;
            }
            const tk = parseInfo(msg.data);
            if (tk === undefined) {
                continue;
            }
            const verdict = await judge(tk.metadata, opts.keys, opts.tenant ?? "", tk.name, tk.workload);
            if (verdict === "refused" ||
                (verdict === "unattested" && (opts.lenient !== true || opts.keys === undefined))) {
                continue;
            }
            tk.attested = verdict === "attested";
            const key = tk.workload !== "" ? tk.workload : tk.id;
            const prev = byWorkload.get(key);
            if (prev !== undefined &&
                ((prev.attested === true && !tk.attested) ||
                    (prev.attested === tk.attested && revisionOf(tk) <= revisionOf(prev)))) {
                continue;
            }
            byWorkload.set(key, tk);
        }
    }
    finally {
        sub.unsubscribe();
    }
    // Of each name, the attested toolkits; the unattested only where none
    // carries it, each with a warning naming it.
    const all = [...byWorkload.values()];
    const attested = new Set(all.filter((tk) => tk.attested === true).map((tk) => tk.name));
    return all.filter((tk) => {
        if (tk.attested === true) {
            return true;
        }
        if (attested.has(tk.name)) {
            return false;
        }
        console.warn(`toolkit: kept an unattested toolkit; its image predates decision 0026: name=${tk.name} workload=${tk.workload}`);
        return true;
    });
}
/** find answers the one toolkit carrying the name among those discovered: none throws NoToolkitError, two AmbiguousToolkitError. */
export function find(toolkits, name) {
    const found = toolkits.filter((tk) => tk.name === name);
    if (found.length === 0) {
        throw new NoToolkitError(name);
    }
    if (found.length > 1) {
        throw new AmbiguousToolkitError(name);
    }
    return found[0];
}
//# sourceMappingURL=toolkit.js.map